/**
 * `/$tenantSlug/settings/rbac/overrides` — per-user permission overrides.
 *
 * Per KEN_LAYER_7 §3.3 + invariant I-17 (denied overrides win over role
 * grants). Roles set the broad strokes; overrides handle the exceptions.
 *
 * Page surfaces:
 *  - The current list of active overrides for the tenant (rows with
 *    user email, permission, grant/deny chip, reason, optional expiry,
 *    and a Revoke button).
 *  - A small form to add a new override: pick the user from the tenant's
 *    users, pick the permission from the build-time catalogue, choose
 *    grant or deny, optionally write a reason and pick an expiry date.
 *
 * The audit emitter records every grant and revoke server-side
 * (`shell.rbac.override_granted` / `shell.rbac.override_revoked`).
 */

import { RequirePermission } from "@ken/auth";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  formatDateOnly,
  Input,
  Label,
  parseDateOnlyLocal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@ken/ui";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";

import { MODULES } from "../generated/modules";

export const Route = createFileRoute("/$tenantSlug/settings/rbac/overrides")({
  component: OverridesRoute,
});

/**
 * Hard permission gate (defense-in-depth): per-user overrides are an admin
 * surface and must require `shell.rbac.view` (same gate as the rbac parent).
 * This route had no gate of its own; RequirePermission renders an accessible
 * "no access" region for anyone lacking it (incl. an advisor). ADR-0090.
 */
function OverridesRoute(): ReactElement {
  return (
    <RequirePermission slug="shell.rbac.view">
      <OverridesContent />
    </RequirePermission>
  );
}

interface TenantUser {
  ken_user_id: string;
  email: string;
  display_name: string | null;
  roles: string[];
}

interface PermissionOverride {
  id: string;
  user_id: string;
  user_email: string | null;
  user_display_name: string | null;
  permission_id: string;
  permission: "granted" | "denied";
  granted_by_user_id: string | null;
  granted_by_email: string | null;
  reason: string | null;
  expires_at: string | null;
  granted_at: string;
}

const SHELL_PERMISSION_IDS = [
  "shell.rbac.view",
  "shell.rbac.edit",
  "shell.settings.view",
  "shell.settings.edit",
  "shell.approvals.view",
  "shell.approvals.decide",
  "shell.team.view",
  "shell.team.edit",
] as const;

function permissionCatalogue(): string[] {
  const out = new Set<string>(SHELL_PERMISSION_IDS);
  for (const m of MODULES) {
    const manifest = m.manifest as unknown as {
      permissions?: Array<{ id?: string }>;
    };
    for (const p of manifest.permissions ?? []) {
      if (p.id) out.add(p.id);
    }
  }
  return Array.from(out).sort();
}

function OverridesContent(): ReactElement {
  const { tenantSlug } = useParams({ from: "/$tenantSlug" });
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [overrides, setOverrides] = useState<PermissionOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formUserId, setFormUserId] = useState<string>("");
  const [formPermissionId, setFormPermissionId] = useState<string>("");
  const [formMode, setFormMode] = useState<"granted" | "denied">("granted");
  const [formReason, setFormReason] = useState("");
  const [formExpiresAt, setFormExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const catalogue = useMemo(() => permissionCatalogue(), []);

  const loadAll = async (): Promise<void> => {
    try {
      const [usersRes, overridesRes] = await Promise.all([
        fetch("/api/shell/rbac/users", { credentials: "include" }),
        fetch("/api/shell/rbac/overrides", { credentials: "include" }),
      ]);
      if (usersRes.status === 401 || overridesRes.status === 401) {
        setError("Your session expired.");
        return;
      }
      if (usersRes.status === 403 || overridesRes.status === 403) {
        setError("You don't have permission to view overrides.");
        return;
      }
      if (!usersRes.ok || !overridesRes.ok) {
        setError("Couldn't load overrides.");
        return;
      }
      setUsers((await usersRes.json()) as TenantUser[]);
      setOverrides((await overridesRes.json()) as PermissionOverride[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!formUserId || !formPermissionId) return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/shell/rbac/overrides", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: formUserId,
          permission_id: formPermissionId,
          permission: formMode,
          reason: formReason || null,
          // The <Input type="date"> value is a strict YYYY-MM-DD string.
          // parseDateOnlyLocal reads it as LOCAL midnight (never UTC
          // midnight, which stored — and later displayed — the expiry a
          // day earlier than the calendar day the owner picked in any
          // negative-offset timezone).
          expires_at: formExpiresAt
            ? (parseDateOnlyLocal(formExpiresAt)?.toISOString() ?? null)
            : null,
        }),
      });
      if (res.status === 403) {
        setError("You don't have permission to grant overrides.");
        return;
      }
      if (!res.ok) {
        setError(`Couldn't grant override (status ${res.status}).`);
        return;
      }
      // Reset the form and refresh the list.
      setFormUserId("");
      setFormPermissionId("");
      setFormMode("granted");
      setFormReason("");
      setFormExpiresAt("");
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (override: PermissionOverride): Promise<void> => {
    setRevokingId(override.id);
    setError(null);
    try {
      const res = await fetch(
        `/api/shell/rbac/overrides/${override.user_id}/${encodeURIComponent(override.permission_id)}`,
        { method: "DELETE", credentials: "include" },
      );
      if (res.status === 403) {
        setError("You don't have permission to revoke overrides.");
        return;
      }
      if (!res.ok) {
        setError(`Couldn't revoke (status ${res.status}).`);
        return;
      }
      setOverrides((prev) => prev.filter((o) => o.id !== override.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="shell-settings-rbac-overrides-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
            Tenant · Settings · Roles &amp; permissions
          </div>
          <h1 className="text-[26px] font-bold tracking-tight">Per-user overrides</h1>
          <p className="text-sm text-[var(--color-text-soft)] mt-1">
            Grant or deny a specific permission for one person, on top of
            whatever their role would normally allow.
          </p>
        </div>
        <Link
          to="/$tenantSlug/settings/rbac"
          params={{ tenantSlug }}
          data-testid="shell-settings-rbac-overrides-back"
          className="text-sm text-[var(--color-accent-blue)] underline-offset-2 hover:underline"
        >
          ← Back to roles
        </Link>
      </header>

      {error !== null && (
        <div
          role="alert"
          data-testid="shell-settings-rbac-overrides-error"
          className="text-sm text-[var(--color-accent-red)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)]"
        >
          {error}
        </div>
      )}

      <Card surface="glass-strong">
        <CardHeader>
          <CardTitle>Add an override</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            data-testid="shell-settings-rbac-overrides-form"
            className="grid grid-cols-2 gap-3"
            onSubmit={(e) => void onSubmit(e)}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="override-user">Person</Label>
              <Select value={formUserId} onValueChange={setFormUserId}>
                <SelectTrigger
                  id="override-user"
                  data-testid="shell-settings-rbac-overrides-form-user"
                >
                  <SelectValue placeholder="Pick a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.ken_user_id} value={u.ken_user_id}>
                      {u.display_name ?? u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="override-permission">Permission</Label>
              <Select value={formPermissionId} onValueChange={setFormPermissionId}>
                <SelectTrigger
                  id="override-permission"
                  data-testid="shell-settings-rbac-overrides-form-permission"
                >
                  <SelectValue placeholder="Pick a permission" />
                </SelectTrigger>
                <SelectContent>
                  {catalogue.map((slug) => (
                    <SelectItem key={slug} value={slug}>
                      {slug}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="override-mode">Effect</Label>
              <div className="flex gap-2" id="override-mode">
                <Button
                  type="button"
                  variant={formMode === "granted" ? "primary" : "ghost"}
                  size="sm"
                  data-testid="shell-settings-rbac-overrides-form-mode-grant"
                  onClick={() => setFormMode("granted")}
                >
                  Grant
                </Button>
                <Button
                  type="button"
                  variant={formMode === "denied" ? "primary" : "ghost"}
                  size="sm"
                  data-testid="shell-settings-rbac-overrides-form-mode-deny"
                  onClick={() => setFormMode("denied")}
                >
                  Deny
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="override-expires-at">Expires (optional)</Label>
              <Input
                id="override-expires-at"
                type="date"
                value={formExpiresAt}
                onChange={(e) => setFormExpiresAt(e.target.value)}
                data-testid="shell-settings-rbac-overrides-form-expires"
              />
            </div>

            <div className="flex flex-col gap-1.5 col-span-2">
              <Label htmlFor="override-reason">Reason (optional)</Label>
              <Input
                id="override-reason"
                type="text"
                placeholder="Why this override exists"
                value={formReason}
                onChange={(e) => setFormReason(e.target.value)}
                data-testid="shell-settings-rbac-overrides-form-reason"
              />
            </div>

            <div className="col-span-2">
              <Button
                type="submit"
                variant="primary"
                data-testid="shell-settings-rbac-overrides-form-submit"
                disabled={!formUserId || !formPermissionId || submitting}
              >
                {submitting ? "Saving…" : "Add override"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-[18px] font-semibold mb-3">Active overrides</h2>
        {loading ? (
          <Skeleton className="h-32 w-full rounded-[var(--radius-md)]" />
        ) : overrides.length === 0 ? (
          <Card surface="glass-strong" padding="lg">
            <CardContent>
              <div
                data-testid="shell-settings-rbac-overrides-empty"
                className="text-sm text-[var(--color-text-soft)] text-center py-8"
              >
                No active overrides. Use the form above to add one.
              </div>
            </CardContent>
          </Card>
        ) : (
          <div
            className="flex flex-col gap-2"
            data-testid="shell-settings-rbac-overrides-list"
          >
            {overrides.map((o) => (
              <Card
                key={o.id}
                surface="glass-strong"
                data-testid={`shell-settings-rbac-overrides-row-${o.id}`}
              >
                <CardContent>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Chip tone={o.permission === "granted" ? "green" : "red"}>
                          {o.permission === "granted" ? "Granted" : "Denied"}
                        </Chip>
                        <span className="font-medium">
                          {o.user_display_name ?? o.user_email ?? o.user_id}
                        </span>
                        <span className="text-[var(--color-text-soft)] text-sm">
                          · {o.permission_id}
                        </span>
                        {o.expires_at !== null && (
                          <Chip tone="amber">
                            until {formatDateOnly(o.expires_at)}
                          </Chip>
                        )}
                      </div>
                      {o.reason !== null && (
                        <div className="text-sm text-[var(--color-text-soft)] mt-1">
                          {o.reason}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={`shell-settings-rbac-overrides-row-${o.id}-revoke`}
                      disabled={revokingId === o.id}
                      onClick={() => void revoke(o)}
                    >
                      {revokingId === o.id ? "Revoking…" : "Revoke"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
